import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { type DraftConflict, mergeDraft } from "@/forwards2/deals/draftMerge";
import type {
  DeliveryResponse,
  PriceConflict,
  ProducerPricesRequest,
} from "@/forwards2/forwardsTypes";

/**
 * The producer-prices dialog's draft: the default producer price, each mandate's price and the
 * trade owner, as one flat record of field key to value, so the 409 merge (`draftMerge`) treats each
 * on its own.
 *
 * A price is kept as it was saved or typed — a number input hands back a number once touched — and
 * compared by value (`sameValue`), so a saved `"18.500"` and a typed `18.5` are one price. Null is a
 * blank: a blank mandate follows the default producer price, and a blank default is the delivery's
 * own price. A typed price is the mandate's own even when it equals the default, so it stays put when
 * the default moves. The trade owner is a company id, null for none.
 *
 * A mandate field exists for every mandate backing the delivery, and for every price kept for a
 * mandate that stopped backing it. Such a kept price is removed by leaving its field out of the
 * draft; blank, it has nothing to say, so a merge drops it too.
 */
export type ProducerPriceValue = string | number | null;
export type ProducerPricesDraft = Record<string, ProducerPriceValue>;

export const TRADE_OWNER_FIELD = "trade_owner";
export const DEFAULT_PRICE_FIELD = "default_price";
const MANDATE_PREFIX = "mandate:";
export const mandateField = (mandateId: number): string =>
  `${MANDATE_PREFIX}${mandateId}`;
export const isMandateField = (field: string): boolean =>
  field.startsWith(MANDATE_PREFIX);
export const mandateOfField = (field: string): number =>
  Number(field.slice(MANDATE_PREFIX.length));

/** A typed or saved value as text, null when blank. */
export const valueText = (
  value: string | number | null | undefined
): string | null => {
  const text = String(value ?? "").trim();
  return text === "" ? null : text;
};

/** Whether two draft values are the same price, or the same trade owner. */
export const sameValue = (
  left: ProducerPriceValue,
  right: ProducerPriceValue
): boolean => {
  const [a, b] = [valueText(left), valueText(right)];
  return a === null || b === null ? a === b : Number(a) === Number(b);
};

/** The mandates backing the delivery, as opposed to those whose price is only kept. */
export const backingMandateIds = (delivery: DeliveryResponse): Set<number> =>
  new Set(
    delivery.mandate_prices
      .filter((price) => price.backs_delivery)
      .map((price) => price.mandate_id)
  );

/** The delivery's saved prices and trade owner, in the draft's terms: a mandate paying the default reads blank. */
export function savedProducerPrices(
  delivery: DeliveryResponse
): ProducerPricesDraft {
  const draft: ProducerPricesDraft = {
    [DEFAULT_PRICE_FIELD]: delivery.default_producer_price,
    [TRADE_OWNER_FIELD]: delivery.trade_owner?.company_id ?? null,
  };
  for (const price of delivery.mandate_prices)
    draft[mandateField(price.mandate_id)] = price.unit_price;
  return draft;
}

/**
 * The price conflicts saving the draft would leave, the ones already saved included: a company that
 * may sell through mandates of the delivery that would pay differently (`shared_producers`). It holds
 * the shipment when its line's escrow came through two of them; else it is foreseen. A mandate pays
 * its typed price, else the draft's default, else the delivery's own price, as
 * `mandate_prices.DeliveryPricing` judges the saved ones. A shipped or voided delivery has none.
 */
export function draftPriceConflicts(
  delivery: DeliveryResponse,
  draft: Readonly<ProducerPricesDraft>
): PriceConflict[] {
  if (isDeliveryFrozen(delivery.transaction_status)) return [];
  const fallback = valueText(draft[DEFAULT_PRICE_FIELD]) ?? delivery.unit_price;
  const names = new Map(
    delivery.mandate_prices.map((price) => [
      price.mandate_id,
      price.mandate_name,
    ])
  );
  const paid = (mandateId: number): string | null =>
    valueText(draft[mandateField(mandateId)]) ?? fallback;
  const disagree = (mandateIds: number[]): boolean =>
    new Set(
      mandateIds.map((mandateId) => {
        const price = paid(mandateId);
        return price === null ? null : Number(price);
      })
    ).size > 1;
  const escrowMandates = new Map(
    delivery.producer_lines.map((line) => [line.company_id, line.mandate_ids])
  );
  return delivery.shared_producers.flatMap((producer) =>
    disagree(producer.mandate_ids)
      ? [
          {
            company_id: producer.company_id,
            company_name: producer.company_name ?? String(producer.company_id),
            mandates: producer.mandate_ids.map((mandateId) => ({
              mandate_id: mandateId,
              mandate_name: names.get(mandateId) ?? String(mandateId),
              unit_price: paid(mandateId),
            })),
            holds_shipment: disagree(
              escrowMandates.get(producer.company_id) ?? []
            ),
          },
        ]
      : []
  );
}

/**
 * The request that saves the draft: the default and the mandate prices, stated whole, the trade
 * owner, and the `plan_version` the draft was composed against. Every mandate field carrying a price
 * is sent as typed, even one equal to the default; a blank one is left out, so it follows the default.
 */
export function producerPricesRequest(
  draft: Readonly<ProducerPricesDraft>,
  planVersion: number
): ProducerPricesRequest {
  const defaultPrice = valueText(draft[DEFAULT_PRICE_FIELD]);
  const mandatePrices: Record<number, string> = {};
  for (const [field, value] of Object.entries(draft)) {
    const price = valueText(value);
    if (isMandateField(field) && price !== null)
      mandatePrices[mandateOfField(field)] = price;
  }
  const tradeOwner = draft[TRADE_OWNER_FIELD];
  return {
    plan_version: planVersion,
    mandate_prices: mandatePrices,
    default_price: defaultPrice,
    trade_owner_company_id: typeof tradeOwner === "number" ? tradeOwner : null,
  };
}

/**
 * The draft after a 409: merged onto the delivery as it is saved now (`mergeDraft`).
 *
 * @property shipped - The delivery shipped meanwhile: its prices are history, so the dialog turns
 *   read-only on the saved values and nothing of the draft survives.
 * @property takenOver - How many fields now hold a saved value the user did not type, a mandate that
 *   started backing the delivery meanwhile included.
 * @property mandatesRemoved - How many of the user's mandates left the dialog meanwhile: one that
 *   stopped backing the delivery with no price kept, or a kept price someone else removed.
 */
export interface ProducerPricesMerge {
  draft: ProducerPricesDraft;
  conflicts: Record<string, DraftConflict<ProducerPriceValue>>;
  takenOver: number;
  mandatesRemoved: number;
  shipped: boolean;
}

/**
 * `mergeDraft` knows only the fields saved now, so a kept price the user removed merges as a blank,
 * and a blank kept price leaves the draft: it would have nothing to say.
 */
export function mergeProducerPrices(
  base: Readonly<ProducerPricesDraft>,
  draft: Readonly<ProducerPricesDraft>,
  latest: DeliveryResponse
): ProducerPricesMerge {
  const theirs = savedProducerPrices(latest);
  const shipped = isDeliveryFrozen(latest.transaction_status);
  const removedByMe = Object.keys(base).filter(
    (field) => isMandateField(field) && !(field in draft)
  );
  const mine: ProducerPricesDraft = {
    ...draft,
    ...Object.fromEntries(removedByMe.map((field) => [field, null])),
  };
  const merged = mergeDraft({
    base,
    theirs,
    mine,
    locked: shipped ? new Set(Object.keys(theirs)) : undefined,
    equals: sameValue,
  });

  const values = { ...merged.values };
  const takenOver = new Set(merged.takenOver);
  const backing = backingMandateIds(latest);
  for (const [field, value] of Object.entries(values)) {
    const blankKeptPrice =
      isMandateField(field) &&
      !backing.has(mandateOfField(field)) &&
      valueText(value) === null;
    if (blankKeptPrice && !(field in merged.conflicts)) {
      delete values[field];
      takenOver.delete(field);
    }
  }

  return {
    draft: values,
    conflicts: merged.conflicts,
    takenOver: takenOver.size,
    mandatesRemoved: merged.removed.filter(
      (field) => !removedByMe.includes(field)
    ).length,
    shipped,
  };
}
